import React, { useRef, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import {
  PayPalButtons,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { ErrorAlert } from '../../ui-elements/ErrorAlert';
import { FormInputsType, NextAction, PaymentElementResponse } from '../../../@types';
import { CommonPaymentElementPropTypes } from '../../../propTypes';
import { useTranslations } from '../../../translations/useTranslations';
import { sanitizeErrors } from '../../../utils/errors';
import { useCommonOptionsStore } from '../../../store';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import {
  paypalPaymentMethodHashPayloadFactory,
} from '../../../factories/query-payment-method-hash-payload/paypalPaymentMethodHashPayloadFactory';
import { nextActionHandler } from '../../../handlers/nextActionHandler';
import { NEXT_ACTION_TYPE_PAYPAL_ORDER, Processors } from '../../../constants';

export const PayPalFormPropTypes = {
  ...CommonPaymentElementPropTypes,
  pm: PropTypes.string.isRequired,
  apiKey: PropTypes.string.isRequired,
  processor: PropTypes.oneOf(Processors).isRequired,
};

export type PayPalFormProps = InferProps<typeof PayPalFormPropTypes>;

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

const PayPalForm = React.forwardRef(({
  onLoadStart,
  onLoadEnd,
  onError,
  onSubmit,
  onCancel,
  pm,
  isHostFormValid,
}: PayPalFormProps, forwardRef: any) => {
  const lang = useCommonOptionsStore(state => state.lang);
  const propertyId = useCommonOptionsStore(state => state.propertyId);
  const apiKey = useCommonOptionsStore(state => state.apiKey);

  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
  });

  const onSuccess = useRef<() => void>();
  const [{ isPending }] = usePayPalScriptReducer();
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const { handleSubmit, formState } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitting,
  } = formState;

  const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onError && errCount) onError(sanitizeErrors(errors));
  }, [onError, errors, errCount]);

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    const data = { ...formData };

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        paymentMethod: pm,
      };

      const nextAction: NextAction = await onSubmit({ ...responseData });
      await nextActionHandler(nextAction);
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(submitHandler, submitErrorHandler)();
    },
    async validate() {
      return true;
    },
  }));

  const onCreateOrder = async (): Promise<string> => {
    const {
      hash: paymentMethodHash,
      error: hashError,
    } = await queryPaymentMethodHash(+propertyId!, apiKey!, paypalPaymentMethodHashPayloadFactory());
    if (hashError || !paymentMethodHash) {
      if (onError) onError({ hashError: hashError || t('paymentMethodError') });
      throw new Error(hashError || t('paymentMethodError'));
    }

    if (onSubmit) {
      const nextAction: NextAction = await onSubmit({
        paymentMethod: pm,
        paymentMethodHash,
      });
      await nextActionHandler(nextAction);

      if (nextAction?.type !== NEXT_ACTION_TYPE_PAYPAL_ORDER) throw new Error('Unable to create PayPal Order');

      if (nextAction.onSuccess) onSuccess.current = nextAction.onSuccess;

      const nextActionDetails = nextAction.details || nextAction.paypal_details; // for backwards compatibility
      return nextActionDetails.order_id;
    }

    throw new Error('Unable to create PayPal Order');
  };

  // @ payment confirmation
  const onApproveOrder = (data: any, actions: any) => actions.order.capture()
    .then(() => {
      if (onSuccess.current) onSuccess.current();
    });

  const onCancelOrder = () => {
    if (onCancel) onCancel();
  };
  const onErrorOrder = (error: any) => {
    const res = error.message.match(/\{.*}/gm);
    const errorData = JSON.parse(res[0]);
    const { issue, description } = errorData.paypal_details[0];
    setErrorMessage(`${issue}: ${description}`);
  };

  return (
    <>
      <FormProvider {...formMethods}>
      {isPending && (
        <Center w="100%" h="100%" p={4}>
          <Spinner
            thickness="4px"
            emptyColor="gray.200"
            color="blue.500"
            size="lg"
          />
        </Center>
      )}
      <Box paddingTop={(layoutProps as any).gap}>
        {errorMessage && (
          <ErrorAlert>
            {errorMessage || t('paymentMethodError')}
          </ErrorAlert>
        )}
        <PayPalButtons
          disabled={!isHostFormValid}
          fundingSource="paypal"
          style={{ layout: 'vertical' }}
          createOrder={onCreateOrder}
          onApprove={(data, actions) => onApproveOrder(data, actions)}
          onCancel={onCancelOrder}
          onError={err => onErrorOrder(err)}
        />
      </Box>
    </FormProvider>
    </>
  );
});

PayPalForm.propTypes = PayPalFormPropTypes;

export { PayPalForm };
